import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TimesheetReport,
  TimesheetWeeklyRow,
  TimesheetSessionRow,
  TimesheetSummaryRow,
  TimesheetComplianceRow,
  TimesheetInactiveRow,
} from '@cls/shared';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { waLink } from '../lib/wa.js';
import { TsApproveModal, type TsApproveSession } from '../components/domain/TsApproveModal.js';
import { ManageAgenciesModal } from '../components/domain/ManageAgenciesModal.js';
import { WorkerDetail } from '../components/domain/WorkerDetail.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { useResetSub } from '../features/filters/useFilters.js';

type Tab = 'weekly' | 'sessions' | 'summary' | 'compliance' | 'inactive';
type SortDir = 1 | -1 | 0;
type SortState = Record<Tab, { col: string; dir: SortDir }>;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function isoWeekOf(dateStr: string): string {
  const d = new Date(dateStr);
  const copy = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = copy.getUTCDay() || 7;
  copy.setUTCDate(copy.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(copy.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((copy.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${copy.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function cmp(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function sortRows<T>(rows: T[], col: string | null, dir: SortDir, accessor: (row: T, c: string) => unknown): T[] {
  if (!col || dir === 0) return rows;
  return [...rows].sort((a, b) => dir * cmp(accessor(a, col), accessor(b, col)));
}

export function Timesheet() {
  const { canExport, canApproveTimesheet, isInternal } = useRolePerms();
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>('weekly');
  const [search, setSearch] = useState('');
  const [fAgency, setFAgency] = useState('');
  const [fWorker, setFWorker] = useState('');
  const [fProject, setFProject] = useState('');
  const [weekMode, setWeekMode] = useState<'single' | 'range' | 'all'>('all');
  const [week, setWeek] = useState('');
  const [weekFrom, setWeekFrom] = useState('');
  const [weekTo, setWeekTo] = useState('');
  const [sort, setSort] = useState<SortState>({
    weekly: { col: 'name', dir: 1 },
    sessions: { col: 'date', dir: -1 },
    summary: { col: 'total', dir: -1 },
    compliance: { col: 'date', dir: -1 },
    inactive: { col: 'days', dir: -1 },
  });

  const [approve, setApprove] = useState<{
    sessionId: string; detail: string; sessions: TsApproveSession[]; workerName: string; date: string;
  } | null>(null);
  const [agenciesOpen, setAgenciesOpen] = useState(false);
  const [detailWorkerId, setDetailWorkerId] = useState<string | null>(null);

  useResetSub(() => {
    setSearch(''); setFAgency(''); setFWorker(''); setFProject('');
    setWeekMode('all'); setWeek(''); setWeekFrom(''); setWeekTo('');
  });

  const { data = {} as TimesheetReport } = useQuery<TimesheetReport>({
    queryKey: ['timesheet', 'report'],
    queryFn: () => api.get<TimesheetReport>('/timesheet/report').then((r) => r.data),
  });
  const { weekly = [], sessions = [], summary = [], compliance = [], inactive = [] } = data;

  const approveApi = useMutation({
    mutationFn: async ({ sessionId, approvedBy, note }: { sessionId: string; approvedBy: string; note: string | null }) =>
      api.post('/timesheet/approve', { sessionId, approvedBy, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheet', 'report'] });
      toast.success('Approved');
    },
  });
  const revoke = useMutation({
    mutationFn: async (sessionId: string) => api.post(`/timesheet/approve/${sessionId}/revoke`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheet', 'report'] });
      toast.info('Approval revoked');
    },
  });
  void approveApi;

  // ── Filter options ─────────────────────────────────────────────────────
  const agencyOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of weekly) if (r.agencyCode) set.add(r.agencyCode);
    for (const s of sessions) if (s.agencyCode) set.add(s.agencyCode);
    return [...set].sort();
  }, [weekly, sessions]);

  const workerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of weekly) set.add(r.workerName);
    for (const s of sessions) set.add(s.workerName);
    for (const s of summary) set.add(s.workerName);
    return [...set].sort();
  }, [weekly, sessions, summary]);

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of weekly) map.set(r.projectId, r.projectCode);
    for (const s of sessions) if (s.projectId) map.set(s.projectId, s.projectCode);
    return [...map.entries()].map(([id, code]) => ({ id, code })).sort((a, b) => a.code.localeCompare(b.code));
  }, [weekly, sessions]);

  function inDateWindow(dateStr: string): boolean {
    if (weekMode === 'all') return true;
    const iso = isoWeekOf(dateStr);
    if (weekMode === 'single') return !week || iso === week;
    if (weekMode === 'range') {
      if (!weekFrom && !weekTo) return true;
      if (weekFrom && iso < weekFrom) return false;
      if (weekTo && iso > weekTo) return false;
      return true;
    }
    return true;
  }

  // ── Filtered rows per tab ─────────────────────────────────────────────
  const weeklyFiltered = useMemo(() => {
    return weekly.filter((r) => {
      const s = search.toLowerCase().trim();
      if (s && ![r.workerName, r.workerRole, r.projectCode, r.projectName].join(' ').toLowerCase().includes(s)) return false;
      if (fAgency && r.agencyCode !== fAgency) return false;
      if (fWorker && r.workerName !== fWorker) return false;
      if (fProject && r.projectId !== fProject) return false;
      if (!inDateWindow(r.weekStart)) return false;
      return true;
    });
  }, [weekly, search, fAgency, fWorker, fProject, weekMode, week, weekFrom, weekTo]);

  const sessionsFiltered = useMemo(() => {
    return sessions.filter((s) => {
      const ss = search.toLowerCase().trim();
      if (ss && ![s.workerName, s.workerRole, s.projectCode, s.projectName].join(' ').toLowerCase().includes(ss)) return false;
      if (fAgency && s.agencyCode !== fAgency) return false;
      if (fWorker && s.workerName !== fWorker) return false;
      if (fProject && s.projectId !== fProject) return false;
      if (!inDateWindow(s.date)) return false;
      return true;
    });
  }, [sessions, search, fAgency, fWorker, fProject, weekMode, week, weekFrom, weekTo]);

  const summaryFiltered = useMemo(() => {
    return summary.filter((r) => {
      const s = search.toLowerCase().trim();
      if (s && ![r.workerName, r.workerRole].join(' ').toLowerCase().includes(s)) return false;
      if (fAgency && r.agencyCode !== fAgency) return false;
      if (fWorker && r.workerName !== fWorker) return false;
      return true;
    });
  }, [summary, search, fAgency, fWorker]);

  const complianceFiltered = useMemo(() => {
    return compliance.filter((c) => {
      const s = search.toLowerCase().trim();
      if (s && !c.workerName.toLowerCase().includes(s)) return false;
      if (fAgency && c.agencyCode !== fAgency) return false;
      if (fWorker && c.workerName !== fWorker) return false;
      if (fProject && !c.projects.some((p) => p.projectId === fProject)) return false;
      if (!inDateWindow(c.date)) return false;
      return true;
    });
  }, [compliance, search, fAgency, fWorker, fProject, weekMode, week, weekFrom, weekTo]);

  const inactiveFiltered = useMemo(() => {
    return inactive.filter((r) => {
      const s = search.toLowerCase().trim();
      if (s && ![r.workerName, r.workerRole].join(' ').toLowerCase().includes(s)) return false;
      if (fAgency && r.agencyCode !== fAgency) return false;
      if (fWorker && r.workerName !== fWorker) return false;
      return true;
    });
  }, [inactive, search, fAgency, fWorker]);

  // ── Sort ───────────────────────────────────────────────────────────────
  function nextDir(curr: SortDir): SortDir { return curr === 1 ? -1 : curr === -1 ? 0 : 1; }
  function setSortFor(t: Tab, col: string) {
    setSort((prev) => {
      const cur = prev[t];
      const dir: SortDir = cur.col === col ? nextDir(cur.dir) : 1;
      return { ...prev, [t]: { col, dir } };
    });
  }

  const weeklySorted = useMemo(() => sortRows<TimesheetWeeklyRow>(weeklyFiltered, sort.weekly.col, sort.weekly.dir, (r, c) => {
    if (c === 'name') return r.workerName;
    if (c === 'role') return r.workerRole;
    if (c === 'agency') return r.agencyCode ?? '';
    if (c === 'project') return r.projectCode;
    if (c.startsWith('d')) return r.days[parseInt(c.slice(1), 10)] ?? 0;
    if (c === 'std') return r.stdHours;
    if (c === 'ot') return r.otHours;
    if (c === 'total') return r.totalHours;
    return '';
  }), [weeklyFiltered, sort.weekly]);

  const sessionsSorted = useMemo(() => sortRows<TimesheetSessionRow>(sessionsFiltered, sort.sessions.col, sort.sessions.dir, (r, c) => {
    if (c === 'name') return r.workerName;
    if (c === 'role') return r.workerRole;
    if (c === 'agency') return r.agencyCode ?? '';
    if (c === 'project') return r.projectCode;
    if (c === 'date') return r.date;
    if (c === 'in') return r.signInAt;
    if (c === 'out') return r.signOutAt ?? '';
    if (c === 'hours') return r.hours ?? 0;
    if (c === 'std') return r.stdHours;
    if (c === 'ot') return r.otHours;
    if (c === 'type') return r.type;
    return '';
  }), [sessionsFiltered, sort.sessions]);

  const summarySorted = useMemo(() => sortRows<TimesheetSummaryRow>(summaryFiltered, sort.summary.col, sort.summary.dir, (r, c) => {
    if (c === 'name') return r.workerName;
    if (c === 'role') return r.workerRole;
    if (c === 'agency') return r.agencyCode ?? '';
    if (c === 'sess') return r.sessions;
    if (c === 'total') return r.totalHours;
    if (c === 'std') return r.stdHours;
    if (c === 'ot') return r.otHours;
    if (c === 'otp') return r.otPercent;
    if (c === 'proj') return r.projectCount;
    return '';
  }), [summaryFiltered, sort.summary]);

  const complianceSorted = useMemo(() => sortRows<TimesheetComplianceRow>(complianceFiltered, sort.compliance.col, sort.compliance.dir, (r, c) => {
    if (c === 'name') return r.workerName;
    if (c === 'date') return r.date;
    if (c === 'sites') return r.projects.length;
    if (c === 'hours') return r.totalHours;
    if (c === 'approved') return r.approvedBy ?? '';
    return '';
  }), [complianceFiltered, sort.compliance]);

  const inactiveSorted = useMemo(() => sortRows<TimesheetInactiveRow>(inactiveFiltered, sort.inactive.col, sort.inactive.dir, (r, c) => {
    if (c === 'name') return r.workerName;
    if (c === 'role') return r.workerRole;
    if (c === 'agency') return r.agencyCode ?? '';
    if (c === 'last') return r.lastSeen ?? '';
    if (c === 'days') return r.daysSince ?? Number.POSITIVE_INFINITY;
    if (c === 'status') return r.status;
    return '';
  }), [inactiveFiltered, sort.inactive]);

  // ── KPI totals ─────────────────────────────────────────────────────────
  const kpisWeekly = useMemo(() => {
    let total = 0, std = 0, ot = 0;
    for (const r of weeklySorted) { total += r.totalHours; std += r.stdHours; ot += r.otHours; }
    return { total, std, ot, rows: weeklySorted.length };
  }, [weeklySorted]);

  const kpisSessions = useMemo(() => {
    let total = 0, std = 0, ot = 0;
    for (const r of sessionsSorted) { total += r.hours ?? 0; std += r.stdHours; ot += r.otHours; }
    return { total, std, ot, rows: sessionsSorted.length };
  }, [sessionsSorted]);

  const kpisSummary = useMemo(() => {
    let total = 0, std = 0, ot = 0;
    for (const r of summarySorted) { total += r.totalHours; std += r.stdHours; ot += r.otHours; }
    return { total, std, ot, workers: summarySorted.length };
  }, [summarySorted]);

  const complianceBadge = compliance.filter((c) => !c.approvedBy).length;

  // ── Sortable header helper ─────────────────────────────────────────────
  function Th({ label, k, t, align }: { label: string; k: string; t: Tab; align?: 'left' | 'right' | 'center' }) {
    const sst = sort[t];
    const active = sst.col === k && sst.dir !== 0;
    const arrow = !active ? '⇅' : sst.dir === 1 ? '↑' : '↓';
    return (
      <th
        onClick={() => setSortFor(t, k)}
        className={`ts-th-sort${active ? ' ts-sorted' : ''}`}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: align ?? 'left' }}
      >
        {label} <span style={{ opacity: .6 }}>{arrow}</span>
      </th>
    );
  }

  function Kpi({ label, value, color }: { label: string; value: string | number; color: string }) {
    return (
      <div style={{
        background: `${color.replace(')', ', .12)').replace('var(--', 'rgba(').replace('rgba(--', 'var(--')}`,
        border: `1px solid ${color}`,
        borderRadius: 10,
        padding: '10px 16px',
        textAlign: 'center',
        flex: 1,
        minWidth: 90,
      }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 400, color }}>{value}</div>
        <div style={{ fontSize: 9, color, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      </div>
    );
  }

  // ── Helper text per tab ────────────────────────────────────────────────
  const helperText: Record<Tab, string> = {
    weekly: 'Standard = Mon–Fri first 12h (all roles) · 🛡 Security: all hours standard up to 12h · Overtime = weekends + hours beyond 12h · Click row to open profile',
    sessions: 'Each row is one paired sign-in/sign-out. Type indicates Day, Night (18:00–05:59), or Weekend. Geo shows whether the IN/OUT was within the project geofence.',
    summary: 'Aggregated per worker across the visible date range. OT% = overtime as a fraction of total hours. Click row to open profile.',
    compliance: 'Multi-site working requires manager approval. Approved records are timestamped and stored for audit. Click a worker name to view their profile.',
    inactive: 'Workers not signed in today. Colour: grey < 7 days · amber 7–13 days · red 14+ days. Click row to open profile.',
  };

  return (
    <div className="page" id="page-timesheet">
      <div className="hbar">
        <div>
          <div className="ph">⏱ Timesheets</div>
          <div className="ps">Hours calculated from sign in/out records</div>
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => { window.open('/api/reports/weekly/pdf', '_blank'); toast.info('Export started', 'Weekly timesheet · PDF'); }}>⬇ PDF</button> : null}
          {canExport ? <button className="btn btn-ghost btn-sm" onClick={() => { window.open('/api/reports/weekly', '_blank'); toast.info('Export started', 'Weekly timesheet · CSV'); }}>⬇ Excel/CSV</button> : null}
          {isInternal ? <button className="btn btn-ghost btn-sm" onClick={() => setAgenciesOpen(true)}>⚙ Agencies</button> : null}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 14, borderBottom: '2px solid var(--border)', flexWrap: 'wrap' }}>
        {([
          { k: 'weekly', lbl: '📅 Weekly Hours' },
          { k: 'sessions', lbl: '🔍 Session Detail' },
          { k: 'summary', lbl: '📊 Worker Summary' },
          { k: 'compliance', lbl: '⚠ Compliance' },
          { k: 'inactive', lbl: '👤 Inactive Workers' },
        ] as const).map((t) => (
          <button
            key={t.k}
            id={`ts-tab-${t.k}`}
            className={`ts-tab ${tab === t.k ? 'ts-tab-active' : ''}`}
            onClick={() => setTab(t.k)}
            style={{ position: 'relative', padding: '10px 14px', fontSize: 11, fontFamily: 'Syne, sans-serif', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', color: tab === t.k ? 'var(--accent)' : 'var(--muted)', borderBottom: tab === t.k ? '2px solid var(--accent)' : '2px solid transparent' }}
          >
            {t.lbl}
            {t.k === 'compliance' && complianceBadge > 0 ? (
              <span style={{ position: 'absolute', top: 4, right: 4, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 14, height: 14, fontSize: 9, fontWeight: 700, lineHeight: '14px', textAlign: 'center' }}>{complianceBadge}</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10, padding: '8px 12px', background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <input type="text" id="ts-search" placeholder="⌕ Search…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ fontSize: 11, padding: '4px 8px', width: 150, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)' }} />
        <select id="ts-agency-filter" value={fAgency} onChange={(e) => setFAgency(e.target.value)} style={{ fontSize: 11, padding: '4px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)' }}>
          <option value="">All Agencies</option>
          {agencyOptions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select id="ts-worker-filter" value={fWorker} onChange={(e) => setFWorker(e.target.value)} style={{ fontSize: 11, padding: '4px 6px', maxWidth: 140, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)' }}>
          <option value="">All Workers</option>
          {workerOptions.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <select id="ts-project-filter" value={fProject} onChange={(e) => setFProject(e.target.value)} style={{ fontSize: 11, padding: '4px 6px', maxWidth: 140, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)' }}>
          <option value="">All Projects</option>
          {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
        </select>
        <select id="ts-week-mode" value={weekMode} onChange={(e) => setWeekMode(e.target.value as typeof weekMode)} style={{ fontSize: 11, padding: '4px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)' }}>
          <option value="all">All Records</option>
          <option value="single">Single Week</option>
          <option value="range">Date Range</option>
        </select>
        {weekMode === 'single' ? (
          <input type="week" id="ts-week" value={week} onChange={(e) => setWeek(e.target.value)} style={{ fontSize: 11, padding: '4px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)' }} />
        ) : null}
        {weekMode === 'range' ? (
          <>
            <input type="week" value={weekFrom} onChange={(e) => setWeekFrom(e.target.value)} style={{ fontSize: 11, padding: '4px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)' }} />
            <span style={{ fontSize: 10, color: 'var(--muted)' }}>to</span>
            <input type="week" value={weekTo} onChange={(e) => setWeekTo(e.target.value)} style={{ fontSize: 11, padding: '4px 6px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)' }} />
          </>
        ) : null}
      </div>

      {/* KPI strip (hidden for compliance + inactive per prototype) */}
      {tab === 'weekly' || tab === 'sessions' || tab === 'summary' ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {tab === 'weekly' ? (
            <>
              <Kpi label="Total Hours" value={kpisWeekly.total.toFixed(1)} color="var(--live)" />
              <Kpi label="Standard Hrs" value={kpisWeekly.std.toFixed(1)} color="var(--live)" />
              <Kpi label="Overtime Hrs" value={kpisWeekly.ot.toFixed(1)} color="var(--warn)" />
              <Kpi label="Rows" value={kpisWeekly.rows} color="var(--accent)" />
            </>
          ) : null}
          {tab === 'sessions' ? (
            <>
              <Kpi label="Total Hours" value={kpisSessions.total.toFixed(1)} color="var(--live)" />
              <Kpi label="Standard Hrs" value={kpisSessions.std.toFixed(1)} color="var(--live)" />
              <Kpi label="Overtime Hrs" value={kpisSessions.ot.toFixed(1)} color="var(--warn)" />
              <Kpi label="Sessions" value={kpisSessions.rows} color="var(--accent)" />
            </>
          ) : null}
          {tab === 'summary' ? (
            <>
              <Kpi label="Total Hours" value={kpisSummary.total.toFixed(1)} color="var(--live)" />
              <Kpi label="Standard Hrs" value={kpisSummary.std.toFixed(1)} color="var(--live)" />
              <Kpi label="Overtime Hrs" value={kpisSummary.ot.toFixed(1)} color="var(--warn)" />
              <Kpi label="Workers" value={kpisSummary.workers} color="var(--accent)" />
            </>
          ) : null}
        </div>
      ) : null}

      <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 8, padding: '0 4px' }}>{helperText[tab]}</div>

      <div id="timesheet-body">
        {tab === 'weekly' ? <WeeklyTable rows={weeklySorted} kpis={kpisWeekly} Th={Th} onWorker={setDetailWorkerId} /> : null}
        {tab === 'sessions' ? <SessionsTable rows={sessionsSorted} Th={Th} onWorker={setDetailWorkerId} /> : null}
        {tab === 'summary' ? <SummaryTable rows={summarySorted} Th={Th} onWorker={setDetailWorkerId} /> : null}
        {tab === 'compliance' ? (
          <ComplianceTable
            rows={complianceSorted}
            Th={Th}
            onWorker={setDetailWorkerId}
            canApprove={canApproveTimesheet}
            onApprove={(c) => setApprove({
              sessionId: c.key,
              detail: `${c.workerName} worked ${c.totalHours.toFixed(1)}h across ${c.projects.length} projects on ${c.date}`,
              sessions: c.projects.map((p) => ({
                projectCode: p.projectCode,
                projectName: p.projectName,
                signInAt: p.signInAt,
                signOutAt: p.signOutAt,
                hours: p.hours,
              })),
              workerName: c.workerName,
              date: c.date,
            })}
            onRevoke={(c) => revoke.mutate(c.key)}
          />
        ) : null}
        {tab === 'inactive' ? <InactiveTable rows={inactiveSorted} Th={Th} onWorker={setDetailWorkerId} /> : null}
      </div>

      {approve ? (
        <TsApproveModal
          onClose={() => setApprove(null)}
          sessionId={approve.sessionId}
          detail={approve.detail}
          sessions={approve.sessions}
          workerName={approve.workerName}
          date={approve.date}
        />
      ) : null}
      {agenciesOpen ? <ManageAgenciesModal onClose={() => setAgenciesOpen(false)} /> : null}
      {detailWorkerId ? <WorkerDetail workerId={detailWorkerId} onClose={() => setDetailWorkerId(null)} /> : null}
    </div>
  );
}

// ── Per-tab tables ────────────────────────────────────────────────────────

type ThComponent = (props: { label: string; k: string; t: Tab; align?: 'left' | 'right' | 'center' }) => JSX.Element;

function emptyRow(colSpan: number, msg: string) {
  return <tr><td colSpan={colSpan} style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>{msg}</td></tr>;
}

function fmtH(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toFixed(1);
}

function WeeklyTable({ rows, kpis, Th, onWorker }: { rows: TimesheetWeeklyRow[]; kpis: { total: number; std: number; ot: number; rows: number }; Th: ThComponent; onWorker: (id: string) => void }) {
  return (
    <div className="card" style={{ overflow: 'auto', padding: 0 }}>
      <table style={{ margin: 0, width: '100%' }}>
        <thead>
          <tr>
            <Th label="Worker" k="name" t="weekly" />
            <Th label="Role" k="role" t="weekly" />
            <Th label="Agency" k="agency" t="weekly" />
            <Th label="Project" k="project" t="weekly" />
            {DAY_LABELS.map((d, i) => <Th key={d} label={d} k={`d${i}`} t="weekly" align="center" />)}
            <Th label="Std" k="std" t="weekly" align="center" />
            <Th label="OT" k="ot" t="weekly" align="center" />
            <Th label="Total" k="total" t="weekly" align="center" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? emptyRow(11 + DAY_LABELS.length - DAY_LABELS.length, 'No timesheet data matches filters.') : rows.map((r) => (
            <tr key={`${r.workerId}-${r.projectId}-${r.weekStart}`} style={{ cursor: 'pointer' }} onClick={() => onWorker(r.workerId)}>
              <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{r.workerName}</td>
              <td style={{ color: 'var(--muted)', fontSize: 11, whiteSpace: 'nowrap' }}>{r.workerRole}</td>
              <td style={{ color: 'var(--muted)' }}>{r.agencyCode ?? '—'}</td>
              <td style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {r.projectCode}
                <div style={{ fontSize: 9, color: 'var(--muted)', fontFamily: 'DM Sans, sans-serif', fontWeight: 400 }}>{r.projectName}</div>
              </td>
              {r.days.map((h, i) => <td key={i} className="ts-day-cell" style={{ textAlign: 'center', fontSize: 10 }}>{h > 0 ? h.toFixed(1) : '—'}</td>)}
              <td className="ts-std" style={{ textAlign: 'center', color: 'var(--live)', fontWeight: 600 }}>{r.stdHours > 0 ? r.stdHours.toFixed(1) : '—'}</td>
              <td className="ts-ot" style={{ textAlign: 'center', color: 'var(--warn)', fontWeight: 600 }}>{r.otHours > 0 ? r.otHours.toFixed(1) : '—'}</td>
              <td style={{ textAlign: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{r.totalHours.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
        {rows.length > 0 ? (
          <tfoot style={{ background: 'var(--surface)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>
            <tr>
              <td colSpan={4} style={{ textAlign: 'right', padding: '6px 8px' }}>{`TOTAL (${kpis.rows} rows)`}</td>
              {(() => {
                const totals = [0, 0, 0, 0, 0, 0, 0];
                for (const r of rows) for (let i = 0; i < 7; i++) totals[i] += r.days[i] ?? 0;
                return totals.map((t, i) => <td key={i} style={{ textAlign: 'center' }}>{t > 0 ? t.toFixed(1) : '—'}</td>);
              })()}
              <td style={{ textAlign: 'center', color: 'var(--live)' }}>{kpis.std.toFixed(1)}</td>
              <td style={{ textAlign: 'center', color: 'var(--warn)' }}>{kpis.ot.toFixed(1)}</td>
              <td style={{ textAlign: 'center' }}>{kpis.total.toFixed(1)}</td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function SessionsTable({ rows, Th, onWorker }: { rows: TimesheetSessionRow[]; Th: ThComponent; onWorker: (id: string) => void }) {
  function GeoIcon({ ok }: { ok: boolean | null }) {
    if (ok === null) return <span style={{ color: 'var(--muted)' }}>—</span>;
    if (ok) return <span style={{ color: 'var(--live)' }}>✓</span>;
    return <span style={{ color: 'var(--warn)' }}>⚠</span>;
  }
  function TypeBadge({ t }: { t: TimesheetSessionRow['type'] }) {
    const colour = t === 'Day' ? 'var(--muted)' : t === 'Night' ? 'var(--info)' : 'var(--warn)';
    return <span style={{ color: colour, fontSize: 10, fontWeight: 600 }}>{t}</span>;
  }
  return (
    <div className="card" style={{ overflow: 'auto', padding: 0 }}>
      <table style={{ margin: 0, width: '100%' }}>
        <thead>
          <tr>
            <Th label="Worker" k="name" t="sessions" />
            <Th label="Role" k="role" t="sessions" />
            <Th label="Agency" k="agency" t="sessions" />
            <Th label="Project" k="project" t="sessions" />
            <Th label="Date" k="date" t="sessions" />
            <Th label="Sign In" k="in" t="sessions" />
            <Th label="Sign Out" k="out" t="sessions" />
            <Th label="Hours" k="hours" t="sessions" align="center" />
            <Th label="Std" k="std" t="sessions" align="center" />
            <Th label="OT" k="ot" t="sessions" align="center" />
            <Th label="Type" k="type" t="sessions" align="center" />
            <th style={{ textAlign: 'center' }}>Geo</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? emptyRow(12, 'No session data matches filters.') : rows.map((r) => (
            <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => onWorker(r.workerId)}>
              <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{r.workerName}</td>
              <td style={{ color: 'var(--muted)', fontSize: 11, whiteSpace: 'nowrap' }}>{r.workerRole}</td>
              <td style={{ color: 'var(--muted)' }}>{r.agencyCode ?? '—'}</td>
              <td style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.projectCode}</td>
              <td>{r.date}</td>
              <td>{new Date(r.signInAt).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}</td>
              <td>{r.signOutAt ? new Date(r.signOutAt).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : <span style={{ color: 'var(--warn)' }}>—</span>}</td>
              <td style={{ textAlign: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{fmtH(r.hours)}</td>
              <td className="ts-std" style={{ textAlign: 'center', color: 'var(--live)', fontWeight: 600 }}>{r.stdHours > 0 ? r.stdHours.toFixed(1) : '—'}</td>
              <td className="ts-ot" style={{ textAlign: 'center', color: 'var(--warn)', fontWeight: 600 }}>{r.otHours > 0 ? r.otHours.toFixed(1) : '—'}</td>
              <td style={{ textAlign: 'center' }}><TypeBadge t={r.type} /></td>
              <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                <GeoIcon ok={r.geofenceIn} /> / <GeoIcon ok={r.geofenceOut} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryTable({ rows, Th, onWorker }: { rows: TimesheetSummaryRow[]; Th: ThComponent; onWorker: (id: string) => void }) {
  return (
    <div className="card" style={{ overflow: 'auto', padding: 0 }}>
      <table style={{ margin: 0, width: '100%' }}>
        <thead>
          <tr>
            <Th label="Worker" k="name" t="summary" />
            <Th label="Role" k="role" t="summary" />
            <Th label="Agency" k="agency" t="summary" />
            <Th label="Sessions" k="sess" t="summary" align="center" />
            <Th label="Total" k="total" t="summary" align="center" />
            <Th label="Std" k="std" t="summary" align="center" />
            <Th label="OT" k="ot" t="summary" align="center" />
            <Th label="OT %" k="otp" t="summary" align="center" />
            <Th label="Projects" k="proj" t="summary" align="center" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? emptyRow(9, 'No summary data matches filters.') : rows.map((r) => (
            <tr key={r.workerId} style={{ cursor: 'pointer' }} onClick={() => onWorker(r.workerId)}>
              <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{r.workerName}</td>
              <td style={{ color: 'var(--muted)', fontSize: 11, whiteSpace: 'nowrap' }}>{r.workerRole}</td>
              <td style={{ color: 'var(--muted)' }}>{r.agencyCode ?? '—'}</td>
              <td style={{ textAlign: 'center' }}>{r.sessions}</td>
              <td style={{ textAlign: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 700, color: 'var(--accent)' }}>{r.totalHours.toFixed(1)}</td>
              <td className="ts-std" style={{ textAlign: 'center', color: 'var(--live)', fontWeight: 600 }}>{r.stdHours.toFixed(1)}</td>
              <td className="ts-ot" style={{ textAlign: 'center', color: 'var(--warn)', fontWeight: 600 }}>{r.otHours.toFixed(1)}</td>
              <td style={{ textAlign: 'center' }}>{r.otPercent}%</td>
              <td style={{ textAlign: 'center' }}>{r.projectCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComplianceTable({ rows, Th, onWorker, canApprove, onApprove, onRevoke }: {
  rows: TimesheetComplianceRow[]; Th: ThComponent; onWorker: (id: string) => void;
  canApprove: boolean;
  onApprove: (c: TimesheetComplianceRow) => void;
  onRevoke: (c: TimesheetComplianceRow) => void;
}) {
  return (
    <div className="card" style={{ overflow: 'auto', padding: 0 }}>
      <table style={{ margin: 0, width: '100%' }}>
        <thead>
          <tr>
            <Th label="Worker" k="name" t="compliance" />
            <Th label="Date" k="date" t="compliance" />
            <th>Sites</th>
            <Th label="Hours" k="hours" t="compliance" align="center" />
            <Th label="Approved" k="approved" t="compliance" align="left" />
            <th style={{ textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? emptyRow(6, 'No multi-site sessions flagged for approval.') : rows.map((c) => (
            <tr key={c.key} style={{ background: c.approvedBy ? 'rgba(34,197,94,.04)' : 'rgba(239,68,68,.04)' }}>
              <td onClick={() => onWorker(c.workerId)} style={{ fontWeight: 500, cursor: 'pointer' }}>{c.workerName}</td>
              <td>{c.date}</td>
              <td>{c.projects.map((p) => <span key={p.projectId} className="pill" style={{ margin: 1 }}>{p.projectCode}</span>)}</td>
              <td style={{ textAlign: 'center', color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>{c.totalHours.toFixed(1)}</td>
              <td>
                {c.approvedBy ? (
                  <span className="badge b-valid">{`✓ ${c.approvedBy}${c.approvedAt ? ` · ${new Date(c.approvedAt).toLocaleDateString('en-GB')}` : ''}`}</span>
                ) : (
                  <span className="badge b-pending">⚠ pending</span>
                )}
              </td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {!c.approvedBy ? (
                  canApprove ? <button className="btn btn-primary btn-xs" onClick={() => onApprove(c)}>⚠ Approve</button> : <span style={{ fontSize: 10, color: 'var(--muted)' }}>—</span>
                ) : (
                  canApprove ? <button className="btn btn-ghost btn-xs" onClick={() => onRevoke(c)}>Revoke</button> : null
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InactiveTable({ rows, Th, onWorker }: { rows: TimesheetInactiveRow[]; Th: ThComponent; onWorker: (id: string) => void }) {
  function urgencyBg(days: number | null): string | undefined {
    if (days == null) return 'rgba(239,68,68,.04)';
    if (days >= 14) return 'rgba(239,68,68,.08)';
    if (days >= 7) return 'rgba(245,158,11,.08)';
    return 'rgba(107,122,153,.05)';
  }
  function urgencyLabel(days: number | null): { text: string; color: string } {
    if (days == null) return { text: 'never', color: 'var(--danger)' };
    if (days >= 14) return { text: `${days}d`, color: 'var(--danger)' };
    if (days >= 7) return { text: `${days}d`, color: 'var(--warn)' };
    return { text: `${days}d`, color: 'var(--muted)' };
  }
  return (
    <div className="card" style={{ overflow: 'auto', padding: 0 }}>
      <table style={{ margin: 0, width: '100%' }}>
        <thead>
          <tr>
            <Th label="Worker" k="name" t="inactive" />
            <Th label="Role" k="role" t="inactive" />
            <Th label="Agency" k="agency" t="inactive" />
            <Th label="CRM Status" k="status" t="inactive" />
            <Th label="Last Seen" k="last" t="inactive" />
            <Th label="Days" k="days" t="inactive" align="center" />
            <th style={{ textAlign: 'right' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? emptyRow(7, 'No inactive workers.') : rows.map((r) => {
            const u = urgencyLabel(r.daysSince);
            const wa = r.phone ? waLink(r.phone, `Hi ${r.workerName}, checking in — are you available for upcoming work?`) : '';
            return (
              <tr key={r.workerId} style={{ background: urgencyBg(r.daysSince), cursor: 'pointer' }} onClick={() => onWorker(r.workerId)}>
                <td style={{ fontWeight: 500 }}>{r.workerName}</td>
                <td style={{ color: 'var(--muted)' }}>{r.workerRole}</td>
                <td style={{ color: 'var(--muted)' }}>{r.agencyCode ?? '—'}</td>
                <td style={{ color: 'var(--muted)', textTransform: 'capitalize' }}>{r.status}</td>
                <td>{r.lastSeen ?? <span style={{ color: 'var(--muted)' }}>never</span>}</td>
                <td style={{ textAlign: 'center', color: u.color, fontWeight: 600 }}>{u.text}</td>
                <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                  {wa ? (
                    <a className="btn btn-ghost btn-xs" href={wa} target="_blank" rel="noreferrer" title="WhatsApp" style={{ textDecoration: 'none' }}>💬</a>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
